import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { Bell, Check, X, ExternalLink, Briefcase, DollarSign, CheckCircle, AlertTriangle, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ICONS = {
  success: CheckCircle, warning: AlertTriangle, campaign: Briefcase,
  payout: DollarSign, info: Info,
};
const COLORS = {
  success: 'text-[#10B981]', warning: 'text-yellow-400',
  campaign: 'text-[#FF4343]', payout: 'text-[#FF4343]', info: 'text-[#9CA3AF]',
};

export default function NotificationCenter() {
  const { user } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetch = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/notifications`, { headers: getAuthHeaders() });
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch {}
  }, [user]);

  useEffect(() => { fetch(); const i = setInterval(fetch, 30000); return () => clearInterval(i); }, [fetch]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read`, {}, { headers: getAuthHeaders() });
      setUnread(0);
      setNotifs(notifs.map(n => ({ ...n, read: true })));
    } catch {}
  };

  const markOne = async (id) => {
    try {
      await axios.post(`${API}/notifications/read/${id}`, {}, { headers: getAuthHeaders() });
      setNotifs(notifs.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(Math.max(0, unread - 1));
    } catch {}
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref} data-testid="notification-center">
      <button onClick={() => setOpen(!open)} data-testid="notification-bell"
        className="relative text-[#9CA3AF] hover:text-white transition-colors p-1">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#FF4343] text-[#090E33] text-[9px] font-bold flex items-center justify-center" data-testid="notif-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm glass border border-white/10 shadow-2xl max-h-[60vh] overflow-hidden z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="text-xs font-semibold">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-[10px] text-[#FF4343] hover:underline" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[50vh]">
            {notifs.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[#6B7280]">No notifications yet</div>
            ) : (
              notifs.map(n => {
                const Icon = ICONS[n.type] || Info;
                const color = COLORS[n.type] || 'text-[#9CA3AF]';
                return (
                  <div key={n.id}
                    onClick={() => { markOne(n.id); if (n.link) window.location.href = n.link; }}
                    className={`px-4 py-3 border-b border-white/5 cursor-pointer hover:bg-white/[0.03] transition-colors ${!n.read ? 'bg-white/[0.02]' : ''}`}
                    data-testid={`notif-${n.id}`}
                  >
                    <div className="flex gap-3">
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-xs font-medium ${!n.read ? 'text-white' : 'text-[#9CA3AF]'}`}>{n.title}</p>
                        <p className="text-[10px] text-[#6B7280] mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[9px] text-[#6B7280] mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                      {!n.read && <span className="w-2 h-2 bg-[#FF4343] rounded-full flex-shrink-0 mt-1.5" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
